/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Checkbox, Loader, Modal } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import React, { useContext, useEffect, useState } from 'react';
import descriptors from '../../locales/en-US/descriptors';
import { IRequestContext } from '../DashboardKPIWidgets/IEnergyManagementProps';
import useConnectorForPost from '../shared/useConnectorAndDynamicProps/useConnectorForPost';
import { ApiContext } from '../../context/ApiContext';
import { apiSuccessStatusCode } from '../shared/Utils/Constants';
import { getNotifyMessage, getStatusCodeMessage } from '../shared/Utils/Utils';

interface IEnergyOptimizationSettingStatusProps {
  hasEOEnabled: boolean;
  bearerToken?: string;
  requestContext: IRequestContext;
}

const EnergyOptimizationSettingsComponentContent = (
  props: IEnergyOptimizationSettingStatusProps
) => {
  const { bearerToken, requestContext, hasEOEnabled } = props;
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isEOEnabled, setEOEnabled] = useState<boolean>(hasEOEnabled);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submit, setSubmit] = useState(false);

  const d = descriptors.energyManagement;
  const [t] = useTranslation();

  const apiContext = useContext(ApiContext);
  const { notifyMessage } = apiContext;

  const connectorConfig = {
    name: 'energy-optimization-enable-disable'
  };
  const requestBody = {
    start: !isEOEnabled
  };
  const dataConnector = useConnectorForPost({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const { all, loading, error, status } = dataConnector.data;

  const energyOptimizationSettingUpdate = (all as unknown) as number[];

  let isDataAvailable = false;
  if (energyOptimizationSettingUpdate) {
    isDataAvailable = true;
  }

  useEffect(() => {
    if (submit && status !== 0 && status !== undefined) {
      if (status === apiSuccessStatusCode && isDataAvailable) {
        notifyMessage &&
          notifyMessage(getNotifyMessage(isEOEnabled, t), 'success');
        setEOEnabled(!isEOEnabled);
      } else {
        notifyMessage &&
          notifyMessage(getStatusCodeMessage(status!, error, t), 'error');
      }
    }
  }, [loading, status, submit]);

  const closeModal = () => {
    setShowModal(false);
  };

  const handleYes = async () => {
    setSubmit(true);
    setSubmitLoading(true);

    await dataConnector.actions.update('', requestBody);

    setSubmitLoading(false);
    closeModal();
  };

  const onEOSettingToggle = (enable: boolean) => {
    setSubmit(false);
    if (!enable) {
      setShowModal(true);
    } else {
      handleYes();
    }
  };

  return (
    <>
      {submitLoading && (
        <Loader overlayColor={'transparent'} text={t(d.LOADING)} />
      )}
      <Checkbox
        checked={isEOEnabled}
        onChange={(val) => onEOSettingToggle(val)}
        label={t(d.EOENABLETITLE)}
        className="eo-setting"
      />
      <Modal
        className="setting-eo-modal"
        size="large"
        closeIcon={true}
        onClose={closeModal}
        open={showModal}
        closeOnDimmerClick={false}
      >
        <div className="setting-eo-modal-header">
          {t(d.EOSETTINGMODALHEADER)}
        </div>
        <Modal.Content className="setting-eo-modal-content">
          <p className="confirmation-note">{t(d.EOSETINGMODALCONTENT)}</p>
          <p className="deactivate-info-mssg">{t(d.EOSETINGMODALCONTENTSUB)}</p>
          <div className="setting-eo-modal-footer">
            <Button
              type="secondary"
              content={t(d.NO)}
              onClick={closeModal}
            ></Button>
            <Button
              type="primary"
              content={t(d.YES)}
              onClick={handleYes}
              loading={submitLoading}
            ></Button>
          </div>
        </Modal.Content>
      </Modal>
    </>
  );
};

export default EnergyOptimizationSettingsComponentContent;
